import type { InferInsertModel, InferSelectModel } from 'drizzle-orm';
import { relations } from 'drizzle-orm';
import {
  boolean,
  doublePrecision,
  jsonb,
  pgEnum,
  pgTable,
  text,
  timestamp,
  uuid,
} from 'drizzle-orm/pg-core';
import { SALE_STATUSES } from '@/lib/sale-status';
import { companies } from './companies';
import { customers } from './customers';
import { paymentMethods } from './payment-methods';

export const saleStatusEnum = pgEnum('sale_status', SALE_STATUSES);

export const saleValueTypeEnum = pgEnum('sale_value_type', ['percentage', 'fixed']);

export const sales = pgTable('sales', {
  id: uuid('id')
    .primaryKey()
    .$defaultFn(() => Bun.randomUUIDv7()),
  saleNumber: text('sale_number'),
  status: saleStatusEnum('status').notNull().default('pending'),
  saleDate: timestamp('sale_date', { mode: 'string' }).notNull(),
  deliveryDate: timestamp('delivery_date', { mode: 'string' }).notNull(),
  cancellationReason: text('cancellation_reason'),
  // Texto bruto coletado pelo bot do WhatsApp (produto/quantidade/prazo/observações)
  // pra pedidos criados no status 'budget', antes do atendente montar os itens de fato.
  budgetNotes: text('budget_notes'),
  total: doublePrecision('total'),
  // Entrada (paga ao confirmar) e saldo (pago ao concluir) — pagamento em 2 etapas.
  depositType: saleValueTypeEnum('deposit_type').notNull().default('percentage'),
  depositValue: doublePrecision('deposit_value').notNull().default(50),
  depositAmount: doublePrecision('deposit_amount'),
  depositPaidAt: timestamp('deposit_paid_at', { mode: 'string' }),
  balancePaidAt: timestamp('balance_paid_at', { mode: 'string' }),
  // Faixa de custo operacional resolvida pela quantidade total de camisetas no momento
  // do pedido — gravado aqui (em vez de recalculado) pra manter o histórico se as faixas
  // cadastradas mudarem depois.
  operationalCost: doublePrecision('operational_cost').default(0),
  // ids de additional_options (scope 'sale') selecionados no pedido, ex: emissão de nota.
  selectedOptions: jsonb('selected_options').$type<string[]>().notNull().default([]),
  invoiceNumber: text('invoice_number'),
  invoicePdfPath: text('invoice_pdf_path'),
  invoiceXmlPath: text('invoice_xml_path'),
  customerId: uuid('customer_id')
    .references(() => customers.id, { onDelete: 'cascade' })
    .notNull(),
  paymentMethodId: uuid('payment_method_id')
    .references(() => paymentMethods.id)
    .notNull(),
  companyId: uuid('company_id')
    .references(() => companies.id)
    .notNull(),
  isActive: boolean('is_active').default(true),
  createdAt: timestamp('created_at')
    .notNull()
    .$defaultFn(() => new Date()),
  updatedAt: timestamp('updated_at')
    .$onUpdate(() => new Date())
    .notNull(),
});

export const salesRelations = relations(sales, ({ one }) => ({
  customer: one(customers, {
    fields: [sales.customerId],
    references: [customers.id],
  }),
  paymentMethod: one(paymentMethods, {
    fields: [sales.paymentMethodId],
    references: [paymentMethods.id],
  }),
  company: one(companies, {
    fields: [sales.companyId],
    references: [companies.id],
  }),
}));

export type Sale = InferSelectModel<typeof sales>;
export type CreateSaleInput = InferInsertModel<typeof sales>;
export type UpdateSaleInput = Partial<CreateSaleInput>;
